import moment from 'moment'
import { map, chunk } from 'lodash'
import { galleryBulkRegisterList } from './gallery'
import { post } from '../lib/http'

const BATCH_SIZE = 10
const MANIFEST_BATCH_SIZE = 50
const RETRIES = 5
const RETRY_DELAY_MS = 500

let S3Client = null
let PutObjectCommand = null

async function loadAwsSdk() {
  if (S3Client && PutObjectCommand) return true
  try {
    const s3Module = await import('@aws-sdk/client-s3')
    S3Client = s3Module.S3Client
    PutObjectCommand = s3Module.PutObjectCommand
    return true
  } catch (error) {
    console.warn('AWS SDK (@aws-sdk/client-s3) not installed. Run: yarn add @aws-sdk/client-s3')
    return false
  }
}

export class BulkFileUploader {

  constructor({
    files,
    credentials,
    gallery,
    onFileProgress,
    onBatchComplete,
    onRegistering,
    onComplete,
    onError
  }) {
    this.files = files
    this.credentials = credentials
    this.gallery = gallery
    this.onFileProgress = onFileProgress || (() => {})
    this.onBatchComplete = onBatchComplete || (() => {})
    this.onRegistering = onRegistering || (() => {})
    this.onComplete = onComplete || (() => {})
    this.onError = onError || (() => {})

    this.successes = []
    this.failures = []
    this.currentManifest = []
    this.manifestBatchNumber = 0
    this.uploadedCount = 0
    this.totalFiles = files.length
    this.s3Client = null
  }

  async initS3Client() {
    const sdkAvailable = await loadAwsSdk()
    if (!sdkAvailable) {
      throw new Error('AWS SDK not installed. Run: yarn add @aws-sdk/client-s3')
    }

    const { region, credentials } = this.credentials
    const s3TemporaryCredentials = {
      accessKeyId: credentials.access_key_id,
      secretAccessKey: credentials.secret_access_key,
    }
    if (credentials.session_token) {
      s3TemporaryCredentials.sessionToken = credentials.session_token
    }
    this.s3Client = new S3Client({
      region: region,
      credentials: s3TemporaryCredentials
    })
  }

  buildS3Path(file) {
    const uuid = crypto.randomUUID()
    const { prefix } = this.credentials
    return `${prefix}${uuid}/${file.name}`
  }

  buildS3Url(s3Path) {
    const { bucket, region } = this.credentials
    return `https://${bucket}.s3.${region}.amazonaws.com/${s3Path}`
  }

  async uploadToS3(file, s3Path) {
    const { bucket } = this.credentials
    const arrayBuffer = await file.arrayBuffer()

    const command = new PutObjectCommand({
      Bucket: bucket,
      Key: s3Path,
      Body: new Uint8Array(arrayBuffer),
      ContentType: file.type
    })

    await this.s3Client.send(command)

    return {
      s3Path,
      s3Url: this.buildS3Url(s3Path),
      filename: file.name,
      size: file.size,
      contentType: file.type,
      lastModified: file.lastModifiedDate ? moment(file.lastModifiedDate).format() : moment().format()
    }
  }

  async uploadFileWithRetry(file) {
    const s3Path = this.buildS3Path(file)
    let lastError = null

    for (let attempt = 1; attempt <= RETRIES; attempt++) {
      try {
        this.onFileProgress(file, 'uploading', { attempt })
        const result = await this.uploadToS3(file, s3Path)
        return result
      } catch (error) {
        lastError = error
        if (attempt < RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * attempt))
        }
      }
    }

    throw lastError
  }

  async saveManifestToS3(manifestData, batchNumber) {
    const { bucket, manifest_prefix } = this.credentials
    const manifestPath = `${manifest_prefix}batch_${String(batchNumber).padStart(3, '0')}.json`

    const manifestJson = JSON.stringify({
      gallery_ref: this.credentials.gallery_ref,
      session_id: this.credentials.session_id,
      batch_number: batchNumber,
      photos: manifestData,
      created_at: moment().format()
    })

    const command = new PutObjectCommand({
      Bucket: bucket,
      Key: manifestPath,
      Body: manifestJson,
      ContentType: 'application/json'
    })

    await this.s3Client.send(command)

    return manifestPath
  }

  async processFile(file) {
    try {
      this.onFileProgress(file, 'pending')

      const uploadResult = await this.uploadFileWithRetry(file)

      this.successes.push({
        file: file.name,
        ...uploadResult
      })

      this.currentManifest.push(uploadResult)
      this.uploadedCount++

      this.onFileProgress(file, 'success', {
        uploaded: this.uploadedCount,
        total: this.totalFiles
      })

      if (this.currentManifest.length >= MANIFEST_BATCH_SIZE) {
        await this.flushManifest()
      }

      return uploadResult

    } catch (error) {
      this.failures.push({
        file: file.name,
        error: error.message
      })

      this.onFileProgress(file, 'failed', { error: error.message })

      return null
    }
  }

  async flushManifest() {
    if (this.currentManifest.length === 0) return

    this.manifestBatchNumber++
    const batchNumber = this.manifestBatchNumber
    const manifestData = [...this.currentManifest]
    this.currentManifest = []

    try {
      const manifestPath = await this.saveManifestToS3(manifestData, batchNumber)
    } catch (error) {
      console.error('Failed to save manifest:', error)
      this.currentManifest = [...manifestData, ...this.currentManifest]
      this.manifestBatchNumber--
      throw error
    }
  }

  async uploadFiles() {
    await this.initS3Client()

    map(this.files, (file) => {
      this.onFileProgress(file, 'pending')
    })

    const fileBatches = chunk(this.files, BATCH_SIZE)

    for (let batchIndex = 0; batchIndex < fileBatches.length; batchIndex++) {
      const batch = fileBatches[batchIndex]

      const batchPromises = batch.map(file => this.processFile(file))
      await Promise.all(batchPromises)

      this.onBatchComplete({
        batchIndex: batchIndex + 1,
        totalBatches: fileBatches.length,
        uploadedCount: this.uploadedCount,
        totalFiles: this.totalFiles
      })
    }

    if (this.currentManifest.length > 0) {
      await this.flushManifest()
    }

    let registrationResult = null
    if (this.successes.length > 0) {
      try {
        this.onRegistering({ photoCount: this.successes.length })
        registrationResult = await post(
          null,
          'photographer/gallery/bulk_register',
          { gallery_ref: this.credentials.gallery_ref, session_id: this.credentials.session_id },
        )
      } catch (error) {
        console.error('Failed to register photos:', error)
        registrationResult = { error: error.message }
      }
    }

    const result = {
      successes: this.successes,
      failures: this.failures,
      manifestBatches: this.manifestBatchNumber,
      session_id: this.credentials.session_id,
      gallery_ref: this.credentials.gallery_ref,
      registration: registrationResult
    }

    this.onComplete(result)

    return result
  }

  getProgress() {
    return {
      uploaded: this.uploadedCount,
      total: this.totalFiles,
      successes: this.successes.length,
      failures: this.failures.length,
      manifestBatches: this.manifestBatchNumber,
      currentManifestSize: this.currentManifest.length,
      percent: this.totalFiles > 0 ? Math.round((this.uploadedCount / this.totalFiles) * 100) : 0
    }
  }
}
